/**
 * Recommendation Actions Hook
 *
 * Manages persistent state for recommendation actions including:
 * - Mark as Reviewed
 * - Dismiss (with reason)
 * - Remind Me Later (with scheduled date)
 *
 * State is persisted to localStorage and syncs across browser tabs.
 */

import { useState, useEffect, useCallback, useSyncExternalStore } from "react";

// ============================================================================
// Constants
// ============================================================================

/** localStorage key for recommendation actions */
const STORAGE_KEY = "compass-recommendation-actions";

// ============================================================================
// Types
// ============================================================================

/**
 * Dismiss reasons for recommendations
 */
export type DismissReason = "not_relevant" | "already_actioned" | "other";

/**
 * Remind later duration options
 */
export type RemindLaterDuration = "1_week" | "2_weeks" | "1_month";

/**
 * Action state for a single recommendation
 */
export interface RecommendationActionState {
  /** Unique identifier for the recommendation */
  recommendationId: string;
  /** Whether the recommendation has been marked as reviewed */
  isReviewed: boolean;
  /** Dismiss information if the recommendation was dismissed */
  dismissed?: {
    reason: DismissReason;
    timestamp: number;
  };
  /** Remind later information if scheduled */
  remindLater?: {
    duration: RemindLaterDuration;
    scheduledAt: number;
    remindAt: number;
  };
  /** Last updated timestamp */
  updatedAt: number;
}

/**
 * Map of recommendation IDs to their action states
 */
export type RecommendationActionsStore = Record<string, RecommendationActionState>;

/**
 * Return type for the useRecommendationActions hook
 */
export interface UseRecommendationActionsReturn {
  /** Get the action state for a specific recommendation */
  getActionState: (recommendationId: string) => RecommendationActionState | undefined;
  /** Check if a recommendation is reviewed */
  isReviewed: (recommendationId: string) => boolean;
  /** Check if a recommendation is dismissed */
  isDismissed: (recommendationId: string) => boolean;
  /** Check if a recommendation has a reminder set */
  hasReminder: (recommendationId: string) => boolean;
  /** Check if a reminder is due (past the remind date) */
  isReminderDue: (recommendationId: string) => boolean;
  /** Mark a recommendation as reviewed */
  markAsReviewed: (recommendationId: string) => void;
  /** Unmark a recommendation as reviewed */
  unmarkAsReviewed: (recommendationId: string) => void;
  /** Toggle the reviewed state */
  toggleReviewed: (recommendationId: string) => boolean;
  /** Dismiss a recommendation with a reason */
  dismiss: (recommendationId: string, reason: DismissReason) => void;
  /** Undismiss a recommendation */
  undismiss: (recommendationId: string) => void;
  /** Set a remind later for a recommendation */
  setRemindLater: (recommendationId: string, duration: RemindLaterDuration) => void;
  /** Clear a remind later for a recommendation */
  clearRemindLater: (recommendationId: string) => void;
  /** Clear all actions for a recommendation (reset to initial state) */
  clearActions: (recommendationId: string) => void;
  /** Get all action states */
  getAllActions: () => RecommendationActionsStore;
  /** Get count of reviewed recommendations */
  reviewedCount: number;
  /** Get count of dismissed recommendations */
  dismissedCount: number;
  /** Get count of recommendations with reminders */
  reminderCount: number;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Calculate the remind-at timestamp based on duration
 */
function calculateRemindAt(duration: RemindLaterDuration): number {
  const now = Date.now();
  const day = 24 * 60 * 60 * 1000;

  switch (duration) {
    case "1_week":
      return now + 7 * day;
    case "2_weeks":
      return now + 14 * day;
    case "1_month":
      return now + 30 * day;
    default:
      return now + 7 * day;
  }
}

/**
 * Get the duration label for display
 */
export function getDurationLabel(duration: RemindLaterDuration): string {
  switch (duration) {
    case "1_week":
      return "1 week";
    case "2_weeks":
      return "2 weeks";
    case "1_month":
      return "1 month";
    default:
      return duration;
  }
}

/**
 * Get the dismiss reason label for display
 */
export function getDismissReasonLabel(reason: DismissReason): string {
  switch (reason) {
    case "not_relevant":
      return "Not relevant";
    case "already_actioned":
      return "Already actioned";
    case "other":
      return "Other";
    default:
      return reason;
  }
}

// ============================================================================
// Storage Utilities
// ============================================================================

/**
 * Get the actions store from localStorage
 */
function getStoredActions(): RecommendationActionsStore {
  if (typeof window === "undefined") {
    return {};
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed as RecommendationActionsStore;
      }
    }
  } catch (error) {
    console.error("Failed to parse recommendation actions from localStorage:", error);
  }
  return {};
}

/**
 * Save the actions store to localStorage
 */
function setStoredActions(actions: RecommendationActionsStore): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(actions));
  } catch (error) {
    console.error("Failed to save recommendation actions to localStorage:", error);
  }
}

// ============================================================================
// External Store for Cross-Tab Sync
// ============================================================================

/**
 * Subscribers for the external store
 */
const subscribers = new Set<() => void>();

/**
 * Notify all subscribers of a change
 */
function notifySubscribers(): void {
  subscribers.forEach((callback) => callback());
}

/**
 * Subscribe to actions changes
 */
function subscribe(callback: () => void): () => void {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/**
 * Get the current snapshot of the actions store
 */
function getSnapshot(): RecommendationActionsStore {
  return getStoredActions();
}

/**
 * Get the server snapshot (for SSR, returns empty object)
 */
function getServerSnapshot(): RecommendationActionsStore {
  return {};
}

// ============================================================================
// Storage Event Listener (Cross-Tab Sync)
// ============================================================================

// Set up storage event listener once
if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === STORAGE_KEY) {
      notifySubscribers();
    }
  });
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for managing recommendation action states
 *
 * Features:
 * - Mark as Reviewed toggle
 * - Dismiss with reason (Not relevant, Already actioned, Other)
 * - Remind Me Later (1 week, 2 weeks, 1 month)
 * - Persists to localStorage
 * - Syncs across browser tabs
 *
 * @returns Object with action state and management functions
 *
 * @example
 * ```tsx
 * function RecommendationCard({ recommendation }: { recommendation: Recommendation }) {
 *   const {
 *     isReviewed,
 *     isDismissed,
 *     toggleReviewed,
 *     dismiss,
 *     setRemindLater,
 *   } = useRecommendationActions();
 *
 *   const reviewed = isReviewed(recommendation.id);
 *   const dismissed = isDismissed(recommendation.id);
 *
 *   if (dismissed) {
 *     return null; // Hide dismissed recommendations
 *   }
 *
 *   return (
 *     <div className={reviewed ? "opacity-50" : ""}>
 *       <h3>{recommendation.title}</h3>
 *       <button onClick={() => toggleReviewed(recommendation.id)}>
 *         {reviewed ? "Unmark Reviewed" : "Mark as Reviewed"}
 *       </button>
 *       <button onClick={() => dismiss(recommendation.id, "not_relevant")}>
 *         Dismiss
 *       </button>
 *       <button onClick={() => setRemindLater(recommendation.id, "1_week")}>
 *         Remind in 1 week
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export function useRecommendationActions(): UseRecommendationActionsReturn {
  // Use useSyncExternalStore for cross-tab sync
  const actionsStore = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Internal state to track changes within the same tab
  const [internalStore, setInternalStore] = useState<RecommendationActionsStore>(actionsStore);

  // Sync internal state with external store
  useEffect(() => {
    setInternalStore(actionsStore);
  }, [actionsStore]);

  /**
   * Helper to update a single recommendation's action state
   */
  const updateActionState = useCallback(
    (recommendationId: string, updater: (state: RecommendationActionState | undefined) => RecommendationActionState | undefined) => {
      const current = getStoredActions();
      const currentState = current[recommendationId];
      const newState = updater(currentState);

      let updated: RecommendationActionsStore;
      if (newState === undefined) {
        // Remove the entry
        const { [recommendationId]: _, ...rest } = current;
        updated = rest;
      } else {
        updated = {
          ...current,
          [recommendationId]: {
            ...newState,
            updatedAt: Date.now(),
          },
        };
      }

      setStoredActions(updated);
      setInternalStore(updated);
      notifySubscribers();
    },
    []
  );

  /**
   * Get the action state for a specific recommendation
   */
  const getActionState = useCallback(
    (recommendationId: string): RecommendationActionState | undefined => {
      return internalStore[recommendationId];
    },
    [internalStore]
  );

  /**
   * Check if a recommendation is reviewed
   */
  const isReviewed = useCallback(
    (recommendationId: string): boolean => {
      return internalStore[recommendationId]?.isReviewed ?? false;
    },
    [internalStore]
  );

  /**
   * Check if a recommendation is dismissed
   */
  const isDismissed = useCallback(
    (recommendationId: string): boolean => {
      return internalStore[recommendationId]?.dismissed !== undefined;
    },
    [internalStore]
  );

  /**
   * Check if a recommendation has a reminder set
   */
  const hasReminder = useCallback(
    (recommendationId: string): boolean => {
      return internalStore[recommendationId]?.remindLater !== undefined;
    },
    [internalStore]
  );

  /**
   * Check if a reminder is due (past the remind date)
   */
  const isReminderDue = useCallback(
    (recommendationId: string): boolean => {
      const reminder = internalStore[recommendationId]?.remindLater;
      if (!reminder) return false;
      return Date.now() >= reminder.remindAt;
    },
    [internalStore]
  );

  /**
   * Mark a recommendation as reviewed
   */
  const markAsReviewed = useCallback(
    (recommendationId: string): void => {
      updateActionState(recommendationId, (state) => ({
        recommendationId,
        isReviewed: true,
        dismissed: state?.dismissed,
        remindLater: state?.remindLater,
        updatedAt: Date.now(),
      }));
    },
    [updateActionState]
  );

  /**
   * Unmark a recommendation as reviewed
   */
  const unmarkAsReviewed = useCallback(
    (recommendationId: string): void => {
      updateActionState(recommendationId, (state) => {
        if (!state) return undefined;
        return {
          ...state,
          isReviewed: false,
        };
      });
    },
    [updateActionState]
  );

  /**
   * Toggle the reviewed state
   * @returns true if now reviewed, false if now not reviewed
   */
  const toggleReviewed = useCallback(
    (recommendationId: string): boolean => {
      const current = getStoredActions();
      const isCurrentlyReviewed = current[recommendationId]?.isReviewed ?? false;
      const newReviewedState = !isCurrentlyReviewed;

      updateActionState(recommendationId, (state) => ({
        recommendationId,
        isReviewed: newReviewedState,
        dismissed: state?.dismissed,
        remindLater: state?.remindLater,
        updatedAt: Date.now(),
      }));

      return newReviewedState;
    },
    [updateActionState]
  );

  /**
   * Dismiss a recommendation with a reason
   */
  const dismiss = useCallback(
    (recommendationId: string, reason: DismissReason): void => {
      updateActionState(recommendationId, (state) => ({
        recommendationId,
        isReviewed: state?.isReviewed ?? false,
        dismissed: {
          reason,
          timestamp: Date.now(),
        },
        remindLater: undefined, // Clear reminder when dismissing
        updatedAt: Date.now(),
      }));
    },
    [updateActionState]
  );

  /**
   * Undismiss a recommendation
   */
  const undismiss = useCallback(
    (recommendationId: string): void => {
      updateActionState(recommendationId, (state) => {
        if (!state) return undefined;
        const { dismissed: _dismissed, ...rest } = state;
        return rest;
      });
    },
    [updateActionState]
  );

  /**
   * Set a remind later for a recommendation
   */
  const setRemindLater = useCallback(
    (recommendationId: string, duration: RemindLaterDuration): void => {
      const now = Date.now();
      updateActionState(recommendationId, (state) => ({
        recommendationId,
        isReviewed: state?.isReviewed ?? false,
        dismissed: undefined, // Clear dismiss when setting reminder
        remindLater: {
          duration,
          scheduledAt: now,
          remindAt: calculateRemindAt(duration),
        },
        updatedAt: now,
      }));
    },
    [updateActionState]
  );

  /**
   * Clear a remind later for a recommendation
   */
  const clearRemindLater = useCallback(
    (recommendationId: string): void => {
      updateActionState(recommendationId, (state) => {
        if (!state) return undefined;
        const { remindLater: _remindLater, ...rest } = state;
        return rest;
      });
    },
    [updateActionState]
  );

  /**
   * Clear all actions for a recommendation (reset to initial state)
   */
  const clearActions = useCallback(
    (recommendationId: string): void => {
      updateActionState(recommendationId, () => undefined);
    },
    [updateActionState]
  );

  /**
   * Get all action states
   */
  const getAllActions = useCallback((): RecommendationActionsStore => {
    return internalStore;
  }, [internalStore]);

  // Calculate counts
  const reviewedCount = Object.values(internalStore).filter((s) => s.isReviewed).length;
  const dismissedCount = Object.values(internalStore).filter((s) => s.dismissed !== undefined).length;
  const reminderCount = Object.values(internalStore).filter((s) => s.remindLater !== undefined).length;

  return {
    getActionState,
    isReviewed,
    isDismissed,
    hasReminder,
    isReminderDue,
    markAsReviewed,
    unmarkAsReviewed,
    toggleReviewed,
    dismiss,
    undismiss,
    setRemindLater,
    clearRemindLater,
    clearActions,
    getAllActions,
    reviewedCount,
    dismissedCount,
    reminderCount,
  };
}

export default useRecommendationActions;
